import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { answerText } from '@/components/worksheets/answerText';
import {
  Loader2, Search, ChevronLeft, Printer, Link2, Unlink, FolderOpen, CalendarDays, Mail,
  Paperclip, Upload, X,
} from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const niceDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric' });
};

const QUIET_TYPES = ['heading', 'instruction'];

/** The answers, laid out as the client was actually asked them. */
export const AnswerSheet = ({ response }) => {
  const fields = (response.fields_snapshot || []).filter((f) => !QUIET_TYPES.includes(f.type));
  return (
    <div className="space-y-3" data-testid={`answer-sheet-${response.id}`}>
      {fields.length === 0 && (
        <p className="text-sm text-gray-600 italic">This form had no questions on it.</p>
      )}
      {fields.map((f) => {
        const shown = answerText(f, response.answers?.[f.id]);
        return (
          <div key={f.id}>
            <p className="text-xs uppercase tracking-wide text-gray-600 font-medium">{f.label}</p>
            <p className="text-sm text-gray-900 whitespace-pre-line">
              {shown || <span className="italic text-gray-600">left blank</span>}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const FormCard = ({ response }) => (
  <div className="rounded-lg border bg-white p-4 space-y-3 text-gray-900">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{response.form_title}</p>
        <p className="text-xs text-gray-600">
          {response.timing === 'after' ? 'After the session' : 'Before the session'} ·
          {' '}received {niceDate(response.submitted_at)}
        </p>
      </div>
      <Button size="sm" variant="outline"
        className="text-gray-700 border-gray-300 hover:bg-gray-100 hover:text-gray-900"
        onClick={() => window.open(`/print/intake/${response.id}`, '_blank')}
        data-testid={`print-response-${response.id}`}>
        <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
      </Button>
    </div>
    <AnswerSheet response={response} />
  </div>
);

const ReceivedCard = ({ file, onDelete }) => (
  <div className="rounded-lg border bg-amber-50/90 border-amber-200 p-3 flex items-center gap-3 text-gray-900"
    data-testid={`received-file-${file.id}`}>
    <Paperclip className="w-4 h-4 text-amber-700 shrink-0" />
    <div className="min-w-0 flex-1">
      <a href={file.file_url} target="_blank" rel="noreferrer"
        className="text-sm font-medium text-gray-900 underline truncate block"
        data-testid={`received-file-link-${file.id}`}>
        {file.label || file.file_name}
      </a>
      <p className="text-xs text-gray-600">
        Emailed back · filed {niceDate(file.received_at)}
      </p>
    </div>
    <button onClick={() => onDelete(file)} title="Remove this document"
      className="text-gray-500 hover:text-red-600" data-testid={`received-file-remove-${file.id}`}>
      <X className="w-4 h-4" />
    </button>
  </div>
);

/** Filing a document a client emailed back, against a session or against the file itself.
 *
 * Compact under each session — a full label box repeated down a client with forty sessions was
 * a wall of empty inputs. The naming box lives in the one panel at the foot of the file.
 */
const AddReceived = ({ clientKey, bookingId, onDone, compact = false }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');

  const send = async (chosen) => {
    if (!chosen) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', chosen);
      body.append('label', label);
      if (bookingId) body.append('booking_id', bookingId);
      await axios.post(
        `${API}/practitioner/client-files/${encodeURIComponent(clientKey)}/received`,
        body, auth());
      toast.success('Filed — their reminders for this session stop now');
      setLabel('');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not file that document');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const picker = (
    <input ref={input} type="file" hidden
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic"
      onChange={(e) => send(e.target.files?.[0])}
      data-testid={`received-file-input-${bookingId || 'file'}`} />
  );

  if (compact) {
    return (
      <>
        {picker}
        <button onClick={() => input.current?.click()} disabled={busy}
          className="text-xs text-gray-400 hover:text-gray-100 inline-flex items-center gap-1.5
                     transition-colors"
          data-testid={`received-file-add-${bookingId || 'file'}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          File a document they emailed back
        </button>
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {picker}
      <Input className="h-8 text-xs max-w-[14rem] bg-white/5 border-white/15 text-gray-100
                        placeholder:text-gray-400" placeholder="What is it? (optional)"
        value={label} onChange={(e) => setLabel(e.target.value)}
        data-testid={`received-file-label-${bookingId || 'file'}`} />
      <Button size="sm" variant="outline" disabled={busy}
        className="text-gray-200 border-white/25 hover:bg-white/10 hover:text-white"
        onClick={() => input.current?.click()}
        data-testid={`received-file-add-${bookingId || 'file'}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        Add a received file
      </Button>
    </div>
  );
};

const ClientFile = ({ clientKey, onBack, allClients, onLinked }) => {
  const [file, setFile] = useState(null);
  const [linking, setLinking] = useState(false);
  const [pick, setPick] = useState('');

  const load = () => axios.get(`${API}/practitioner/client-files/${encodeURIComponent(clientKey)}`, auth())
    .then((r) => setFile(r.data))
    .catch(() => toast.error('Could not open that file'));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientKey]);

  const link = async () => {
    if (!pick) return;
    try {
      await axios.post(`${API}/practitioner/client-files/link`,
        { primary_email: clientKey, alias_email: pick }, auth());
      toast.success('Filed together');
      setLinking(false); setPick('');
      await load(); onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not file them together');
    }
  };

  const unlink = async (email) => {
    try {
      await axios.delete(`${API}/practitioner/client-files/link/${encodeURIComponent(email)}`, auth());
      toast.success('Separated again');
      await load(); onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not separate them');
    }
  };

  const removeReceived = async (f) => {
    if (!window.confirm(`Remove "${f.label || f.file_name}" from this file?`)) return;
    try {
      await axios.delete(`${API}/practitioner/client-files/received/${f.id}`, auth());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove that');
    }
  };

  if (!file) {
    return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>;
  }

  const others = allClients.filter((c) => c.key !== clientKey);

  return (
    <div className="space-y-5" data-testid="client-file">
      <button onClick={onBack} className="text-sm text-gray-300 hover:text-white inline-flex items-center"
        data-testid="client-file-back">
        <ChevronLeft className="w-4 h-4 mr-1" /> All client files
      </button>

      <div>
        <h2 className="text-xl font-semibold text-[#f5f0e8]">{file.name}</h2>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {file.emails.map((e) => (
            <span key={e} className="text-xs text-gray-300 inline-flex items-center gap-1">
              <Mail className="w-3 h-3" /> {e}
              {e !== file.key && (
                <button onClick={() => unlink(e)} title="Separate this address again"
                  className="text-gray-400 hover:text-rose-400" data-testid={`unlink-${e}`}>
                  <Unlink className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>

      {!linking ? (
        <Button size="sm" variant="outline" onClick={() => setLinking(true)}
          className="text-gray-200 border-white/25 hover:bg-white/10 hover:text-white"
          data-testid="link-client-open">
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> Same person as another file
        </Button>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
          <p className="text-xs text-gray-300">
            If they have booked from a second email address, file it here. Nothing is deleted —
            you can separate them again.
          </p>
          <div className="flex flex-wrap gap-2">
            <select className="border rounded-md px-2 py-1.5 text-sm bg-white text-gray-900 min-w-[16rem]"
              value={pick} onChange={(e) => setPick(e.target.value)} data-testid="link-client-select">
              <option value="">Choose the other file…</option>
              {others.map((c) => <option key={c.key} value={c.key}>{c.name} — {c.key}</option>)}
            </select>
            <Button size="sm" onClick={link} disabled={!pick} data-testid="link-client-confirm">File together</Button>
            <Button size="sm" variant="ghost" onClick={() => { setLinking(false); setPick(''); }}
              className="text-gray-300 hover:bg-white/10 hover:text-white">Cancel</Button>
          </div>
        </div>
      )}

      {file.sessions.length === 0 && (
        <p className="text-sm text-gray-300 italic">No sessions with you yet.</p>
      )}

      {file.sessions.map((s) => (
        <div key={s.booking_id} className="space-y-3" data-testid={`client-session-${s.booking_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <CalendarDays className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-[#f5f0e8]">{s.session_title || 'Session'}</span>
            <span className="text-xs text-gray-300">{niceDate(s.scheduled_at)}</span>
            <Badge variant="outline" className="text-xs capitalize text-gray-200 border-white/25">{s.status}</Badge>
            {s.asked > 0 && (
              <span className="text-xs text-gray-300">
                {s.forms.length} of {s.asked} forms in
              </span>
            )}
          </div>
          {s.forms.length === 0 ? (
            <p className="text-xs text-gray-400 pl-6">
              {s.asked > 0 ? 'Nothing back yet for this session.' : 'No forms were asked for.'}
            </p>
          ) : (
            <div className="pl-6 space-y-3">
              {s.forms.map((r) => <FormCard key={r.id} response={r} />)}
            </div>
          )}
          <div className="pl-6 space-y-2">
            {(s.received_files || []).map((f) => (
              <ReceivedCard key={f.id} file={f} onDelete={removeReceived} />
            ))}
            <AddReceived clientKey={clientKey} bookingId={s.booking_id} onDone={load} compact />
          </div>
        </div>
      ))}

      {file.loose_files?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-200">Documents not tied to a session</p>
          <div className="pl-6 space-y-2">
            {file.loose_files.map((f) => (
              <ReceivedCard key={f.id} file={f} onDelete={removeReceived} />
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-1.5">
        <p className="text-xs text-gray-300">
          Did they email a printed form back to you? File it here and it lives in their record
          with everything else.
        </p>
        <AddReceived clientKey={clientKey} bookingId="" onDone={load} />
      </div>

      {file.unfiled?.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-200">Forms whose session is no longer on record</p>
          <div className="pl-6 space-y-3">
            {file.unfiled.map((r) => <FormCard key={r.id} response={r} />)}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 border-t border-white/10 pt-3">{file.notice}</p>
    </div>
  );
};

export default function ClientFilesPage() {
  const [clients, setClients] = useState(null);
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(null);

  const load = () => axios.get(`${API}/practitioner/client-files`, auth())
    .then((r) => { setClients(r.data.clients || []); setNotice(r.data.notice || ''); })
    .catch(() => { setClients([]); toast.error('Could not load your client files'); });

  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients || [];
    return (clients || []).filter((c) =>
      c.name.toLowerCase().includes(q) || c.emails.some((e) => e.includes(q)));
  }, [clients, query]);

  if (clients === null) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;
  }

  if (open) {
    return (
      <ClientFile clientKey={open} allClients={clients} onLinked={load}
        onBack={() => { setOpen(null); load(); }} />
    );
  }

  return (
    <div className="space-y-5" data-testid="client-files-page">
      <div>
        <h2 className="text-xl font-semibold text-[#f5f0e8]">Client Files</h2>
        <p className="text-sm text-gray-300">
          One file per person, built from your bookings. Every session they have had with you, and
          every form they have filled in, in one place.
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input className="pl-9 bg-white/5 border-white/15 text-gray-100 placeholder:text-gray-400"
          placeholder="Search by name or email" value={query}
          onChange={(e) => setQuery(e.target.value)} data-testid="client-files-search" />
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-12 border border-white/10 rounded-lg bg-white/5">
          <FolderOpen className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-300">
            {clients.length === 0
              ? 'Nobody has booked with you yet. Files appear here on their own.'
              : 'Nobody here by that name.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <button key={c.key} onClick={() => setOpen(c.key)}
              className="text-left rounded-lg border bg-white text-gray-900 p-4
                         hover:border-gray-400 hover:shadow-sm transition-all"
              data-testid={`client-card-${c.key}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                {c.unread > 0 && (
                  <Badge className="bg-emerald-600 text-white shrink-0"
                    data-testid={`client-unread-${c.key}`}>{c.unread} new</Badge>
                )}
              </div>
              <p className="text-xs text-gray-600 truncate">{c.key}</p>
              {c.emails.length > 1 && (
                <p className="text-xs text-gray-600">+{c.emails.length - 1} other address</p>
              )}
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                <span>{c.sessions} session{c.sessions === 1 ? '' : 's'}</span>
                <span>{c.forms_in} form{c.forms_in === 1 ? '' : 's'} in</span>
                {c.files_in > 0 && (
                  <span>{c.files_in} document{c.files_in === 1 ? '' : 's'}</span>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {c.next_session
                  ? `Next ${niceDate(c.next_session)}`
                  : c.last_session ? `Last seen ${niceDate(c.last_session)}` : 'No sessions yet'}
              </p>
            </button>
          ))}
        </div>
      )}

      {notice && <p className="text-xs text-gray-400 border-t border-white/10 pt-3">{notice}</p>}
    </div>
  );
}
